import {
  Grid2 as Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";

export const SalesUsers = ({ title, staffRows, setSalesData }) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [emailError, setEmailError] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [values, setValues] = useState({
    fullName: "",
    email: "",
  });

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
      }
    }, 3000);

    return () => {
      clearInterval(clearTextInterval); // Clear the interval when the component unmounts
    };
  }, [errorText]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.trim() === "") {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Invalid email format";
    }
    return "";
  };

  const validateFullName = (fullName) => {
    if (!fullName || fullName.trim() === "") {
      return "Full name is required";
    }

    const nameParts = fullName
      .trim()
      .split(/\s+/)
      .filter((part) => part.length > 0);

    if (nameParts.length < 2) {
      return "Full name must have at least 2 parts (e.g., 'John Doe')";
    }

    // Check if each part is at least 2 characters long
    const invalidParts = nameParts.filter((part) => part.length < 2);
    if (invalidParts.length > 0) {
      return "Each name part must be at least 2 characters long";
    }

    return "";
  };

  const handleAddUser = async () => {
    // Validate email and full name for add operations
    const emailValidationError = validateEmail(values?.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    setEmailError("");

    const fullNameValidationError = validateFullName(values?.fullName);
    if (fullNameValidationError) {
      setFullNameError(fullNameValidationError);
      return;
    }
    setFullNameError("");

    try {
      setAddLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users/manage`,
        {
          values: values,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      setSalesData(res?.data?.data);

      if (resp?.data?.message) {
        setErrorText(resp?.data?.message);
      }

      setAddLoading(false);
      setValues({ fullName: "", email: "" });
      setEmailError("");
      setFullNameError("");
    } catch (e) {
      setErrorText(e.response?.data?.message || "An error occurred");
      setAddLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/user/delete`,
        {
          userEmail: userToDelete.email,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      setSalesData(res?.data?.data);

      if (resp?.data?.message) {
        setErrorText(resp?.data?.message);
      }

      setDeleteLoading(false);
      setDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (e) {
      setErrorText(e.response?.data?.message || "An error occurred");
      setDeleteLoading(false);
    } finally {
      setDeleteLoading(false);
      setUserToDelete(null);
      setTimeout(() => {
        setErrorText("");
      }, 5000);
    }
  };

  const staffColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 0.5,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "added_by",
      headerName: "Added By",
      flex: 1,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "date",
      headerName: "Date",
      flex: 0.5,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      width: "100%",
      headerClassName: "management-datagrid-title",
      valueGetter: (value) => {
        return value === 0 ? "Invited" : "Active";
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.3,
      width: "100%",
      headerClassName: "management-datagrid-title",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Tooltip title="Delete User">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params.row);
              }}
              sx={{ color: "error.main" }}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card sx={{ minHeight: "35rem" }}>
      <CardHeader title={title} />
      <CardContent>
        <DataGrid
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            minHeight: "24rem",
          }}
          rows={staffRows}
          columns={staffColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />
        <Grid container spacing={2} alignItems="center" sx={{ mt: "1rem" }}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                backgroundColor: "primary.main",
                padding: "1rem",
              }}
            >
              <Typography
                variant="body1"
                color="white"
                fontWeight="bold"
                textAlign="center"
                sx={{ minHeight: "1.5rem" }}
              >
                Add New User
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              disabled={addLoading}
              label={"Full Name"}
              variant="filled"
              fullWidth
              value={values?.fullName || ""}
              onChange={(e) => {
                setValues({ ...values, fullName: e.target.value });
                if (fullNameError) {
                  setFullNameError("");
                }
              }}
              error={!!fullNameError}
              helperText={fullNameError || " "}
              onBlur={(e) => {
                const validationError = validateFullName(e.target.value);
                setFullNameError(validationError);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              disabled={addLoading}
              label={"Email"}
              variant="filled"
              fullWidth
              value={values?.email || ""}
              onChange={(e) => {
                setValues({ ...values, email: e.target.value });
                if (emailError) {
                  setEmailError("");
                }
              }}
              error={!!emailError}
              helperText={emailError || " "}
              onBlur={(e) => {
                const validationError = validateEmail(e.target.value);
                setEmailError(validationError);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <LoadingButton
              loading={addLoading}
              disabled={!values?.email || !values?.fullName || addLoading}
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                handleAddUser();
              }}
            >
              Add New
            </LoadingButton>
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          color="primary"
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>
      </CardContent>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove sales user:{" "}
            <strong>{userToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteConfirm}
            loading={deleteLoading}
            variant="contained"
            color="error"
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
